import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, message, Modal, Radio, Select, Space, Typography } from 'antd';
import AuthenticationContext from '../../../../contexts/auth/AuthenticationContext';
import resetDemoAccount from '../../../../../domain/use_cases/demo/resetDemoAccount';
import resetMultipleDemoAccounts from '../../../../../domain/use_cases/demo/resetMultipleDemoAccount';
import { ISubscription } from '@leedpulse-monorepo/interfaces';
import getAvailableDatasets from '../../../../../domain/use_cases/demo/getAvailableDatasets';

type DemoResetMode = 'full' | 'temporal';

interface ResetDemoDataModalProps {
    subscriptions: ISubscription["id"][];
    open: boolean;
    resetCallback: () => void;
    callbackDelay?: number;
    onCancel: () => void;
}

export default function ResetDemoDataModal({ subscriptions, open, resetCallback, callbackDelay = 2000, onCancel }: ResetDemoDataModalProps) {
    const auth = useContext(AuthenticationContext);

    const [resetMode, setResetMode] = useState<DemoResetMode>('temporal');
    const [isResetting, setIsResetting] = useState(false);
    const [selectedDataset, setSelectedDataset] = useState<string | undefined>(undefined);
    const [availableDatasets, setAvailableDatasets] = useState<string[]>([]);
    const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);

    const datasetOptions = useMemo(
        () => availableDatasets.map((dataset) => ({ label: dataset, value: dataset })),
        [availableDatasets]
    );

    const handleConfirmReset = async (): Promise<void> => {
        if (!subscriptions.length) return;

        if (resetMode === 'full' && !selectedDataset) {
            message.error('Veuillez sélectionner un dataset.');
            return;
        }

        try {
            setIsResetting(true);
            const response = subscriptions.length > 1 ?
                await resetMultipleDemoAccounts(
                    subscriptions,
                    auth.authentication,
                    resetMode === 'full' ? selectedDataset : undefined,
                ) :
                await resetDemoAccount(
                    subscriptions[0],
                    auth.authentication,
                    resetMode === 'full' ? selectedDataset : undefined,
                );

            if (response.success) {
                const successMessage = `${response.data.successful} / ${response.data.total} compte${response.data.total > 1 ? 's' : ""} de démonstration réinitialisé${response.data.total > 1 ? 's' : ""} avec succès`;
                message.success(successMessage);
                setTimeout(() => {
                    resetCallback();
                }, callbackDelay);
            } else {
                message.error('Une erreur est survenue lors de la réinitialisation des données de démonstration');
            }
        } finally {
            setIsResetting(false);
        }
    };

    const fetchDatasets = async (): Promise<void> => {
        try {
            setIsLoadingDatasets(true);
            const datasets = await getAvailableDatasets(auth.authentication);
            setAvailableDatasets(datasets ?? []);
        } catch (_e) {
            setAvailableDatasets([]);
        } finally {
            setIsLoadingDatasets(false);
        }
    };

    useEffect(() => {
        // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
        // chargement declenche ici. Architecture Context + use case du repo.
        // eslint-disable-next-line react/set-state-in-effect
        fetchDatasets();
    }, []);

    return <Modal
        title="Réinitialiser les données de démonstration"
        open={open}
        okText={`Réinitialiser ${subscriptions.length > 1 ? `(${subscriptions.length})` : ''}`}
        cancelText="Annuler"
        confirmLoading={isResetting}
        onOk={handleConfirmReset}
        onCancel={onCancel}
    >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Radio.Group
                value={resetMode}
                onChange={(e) => {
                    setResetMode(e.target.value as DemoResetMode);
                    setSelectedDataset(undefined);
                }}
            >
                <Radio.Button value="full">Complète</Radio.Button>
                <Radio.Button value="temporal">Temporelle</Radio.Button>
            </Radio.Group>

            {resetMode === 'full' ? (
                <>
                    <Alert type="info" message={
                        <Typography.Text>
                            Toutes les données de la démonstration. <Typography.Text type="danger" strong>Action irréversible :</Typography.Text><Typography.Text type="danger"> supprime les données créées manuellement.</Typography.Text>
                        </Typography.Text>
                    }
                    />
                    <Select
                        placeholder="Choisir un dataset"
                        loading={isLoadingDatasets}
                        style={{ width: '100%' }}
                        disabled={isLoadingDatasets || datasetOptions.length === 0}
                        options={datasetOptions}
                        value={selectedDataset}
                        onChange={(v) => setSelectedDataset(v)}
                    />
                </>
            ) : <Alert type="info" message={<Typography.Text>Met à jour toutes les heures (SMS, appels, tâches...) à moins de 24h à partir de maintenant.</Typography.Text>} />}
        </Space>
    </Modal>;
}